import { safeGetJSON, safeSetJSON } from "../safeStorage";
import { DAY_MS, utcDateKey } from "./dailyChallenge";
import { ARCADE_GAME_KINDS, isArcadeGameKind, type ArcadeGameKind } from "./games";

// Streaks and badges live on this device only (safeStorage), so they cost
// nothing and work for everyone, signed in or not. A streak is the run of UTC
// days with at least one game started, and it stays alive until a whole day
// goes by without one.

export const PROGRESS_KEY = "truevined-arcade:progress";
const MAX_DAYS = 60;

export const BADGE_IDS = ["first-run", "boss", "score-10k", "streak-7", "all-six", "challenge-top10"] as const;
export type BadgeId = (typeof BADGE_IDS)[number];

export const BADGES: Record<BadgeId, { name: string; detail: string }> = {
  "first-run": { name: "First run", detail: "Played your first game" },
  boss: { name: "Boss down", detail: "Beat your first boss" },
  "score-10k": { name: "10K club", detail: "Scored 10,000 in one run" },
  "streak-7": { name: "Week streak", detail: "Played seven days in a row" },
  "all-six": { name: "Full arcade", detail: "Played all six games" },
  "challenge-top10": { name: "Daily top 10", detail: "Top 10 on a daily challenge" },
};

export const TEN_K = 10_000;
export const STREAK_BADGE_DAYS = 7;

export interface ArcadeProgress {
  days: string[];
  bestStreak: number;
  runs: number;
  games: ArcadeGameKind[];
  badges: Partial<Record<BadgeId, number>>;
}

export const emptyProgress = (): ArcadeProgress => ({ days: [], bestStreak: 0, runs: 0, games: [], badges: {} });

const previousDay = (date: string) => utcDateKey(Date.parse(`${date}T00:00:00Z`) - DAY_MS);

// Days in a row ending today, or ending yesterday while today is still open.
export function streakFrom(days: readonly string[], today: string): number {
  const played = new Set(days);
  let cursor = played.has(today) ? today : previousDay(today);
  let streak = 0;
  while (played.has(cursor)) {
    streak += 1;
    cursor = previousDay(cursor);
  }
  return streak;
}

export const playedToday = (progress: ArcadeProgress, today: string) => progress.days.includes(today);

function earn(progress: ArcadeProgress, id: BadgeId, now: number, earned: BadgeId[]) {
  if (progress.badges[id]) return;
  progress.badges[id] = now;
  earned.push(id);
}

function cloneProgress(progress: ArcadeProgress): ArcadeProgress {
  return { days: [...progress.days], bestStreak: progress.bestStreak, runs: progress.runs, games: [...progress.games], badges: { ...progress.badges } };
}

// A run started: marks the day and the game.
export function recordRunStart(progress: ArcadeProgress, game: ArcadeGameKind, now: number): { progress: ArcadeProgress; earned: BadgeId[] } {
  const next = cloneProgress(progress);
  const today = utcDateKey(now);
  const earned: BadgeId[] = [];
  if (!next.days.includes(today)) next.days = [...next.days, today].sort().slice(-MAX_DAYS);
  if (!next.games.includes(game)) next.games = [...next.games, game];
  const streak = streakFrom(next.days, today);
  next.bestStreak = Math.max(next.bestStreak, streak);
  if (streak >= STREAK_BADGE_DAYS) earn(next, "streak-7", now, earned);
  if (ARCADE_GAME_KINDS.every((kind) => next.games.includes(kind))) earn(next, "all-six", now, earned);
  return { progress: next, earned };
}

// A run ended: first run, a boss beaten, a big score.
export function recordRunEnd(progress: ArcadeProgress, run: { score: number; bossBeaten: boolean }, now: number): { progress: ArcadeProgress; earned: BadgeId[] } {
  const next = cloneProgress(progress);
  const earned: BadgeId[] = [];
  next.runs += 1;
  earn(next, "first-run", now, earned);
  if (run.bossBeaten) earn(next, "boss", now, earned);
  if (run.score >= TEN_K) earn(next, "score-10k", now, earned);
  return { progress: next, earned };
}

export function recordChallengeRank(progress: ArcadeProgress, rank: number | null, now: number): { progress: ArcadeProgress; earned: BadgeId[] } {
  const next = cloneProgress(progress);
  const earned: BadgeId[] = [];
  if (rank !== null && rank >= 1 && rank <= 10) earn(next, "challenge-top10", now, earned);
  return { progress: next, earned };
}

export function readProgress(): ArcadeProgress {
  const stored = safeGetJSON<Partial<ArcadeProgress>>(PROGRESS_KEY);
  if (!stored || typeof stored !== "object") return emptyProgress();
  const days = Array.isArray(stored.days) ? stored.days.filter((day): day is string => typeof day === "string" && /^\d{4}-\d{2}-\d{2}$/.test(day)).sort().slice(-MAX_DAYS) : [];
  const games = Array.isArray(stored.games) ? stored.games.filter(isArcadeGameKind) : [];
  const badges: Partial<Record<BadgeId, number>> = {};
  for (const id of BADGE_IDS) {
    const value = stored.badges?.[id];
    if (typeof value === "number" && Number.isFinite(value)) badges[id] = value;
  }
  return {
    days,
    bestStreak: Number.isFinite(stored.bestStreak) ? Math.max(0, Math.floor(stored.bestStreak as number)) : 0,
    runs: Number.isFinite(stored.runs) ? Math.max(0, Math.floor(stored.runs as number)) : 0,
    games,
    badges,
  };
}

export const PROGRESS_CHANGED_EVENT = "truevined:arcade:progress";

export function writeProgress(progress: ArcadeProgress): void {
  safeSetJSON(PROGRESS_KEY, progress);
  if (typeof window !== "undefined") window.dispatchEvent(new Event(PROGRESS_CHANGED_EVENT));
}

export function streakLabel(streak: number): string {
  return `${streak} day streak`;
}
